import React from 'react';

import { AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

import { type CardSizes, Thumbnail } from '@pkg/components';
import { type Collectible, type Collection } from '@pkg/graphql/codegen/graphql';
import { useBreakpoints } from '@pkg/hooks';
import { AssetCommonMaxWidth } from '@pkg/types';

import { Rarity, RarityText, Wrapper } from './styles';

interface CardMediaProps {
    mashWidth?: AssetCommonMaxWidth;
    image_url?: string | null;
    animation_url?: string | null;
    shadow?: boolean;
    cardSize?: CardSizes;
    objectFit?: 'cover' | 'contain';
    isHovering?: boolean;
    canHover?: boolean;
    isOwner?: boolean;
    item?: Collectible | null;
    collectionData?: Collection | null;
    hideRarityRank?: boolean;
}

export const CardMedia = ({
    image_url,
    animation_url,
    objectFit = 'contain',
    mashWidth = AssetCommonMaxWidth.Nft,
    shadow,
    canHover = false,
    isOwner = false,
    cardSize,
    isHovering,
    item,
    collectionData,
    hideRarityRank,
}: CardMediaProps) => {
    const { rarityRank, id } = item || {};
    const [chain, contract, _tokenId, _aptosTokenId] = id?.split('.') || [];
    const aptosTokenId = _aptosTokenId && collectionData?.name ? `${collectionData.name}.${_aptosTokenId}` : undefined;
    const tokenId = aptosTokenId || _tokenId;
    const { isMobile } = useBreakpoints();
    const linkDisabled = !chain || !contract || !tokenId;

    const wrapperMarkup = (
        <Wrapper $disabled={linkDisabled} $hoverZoom={!linkDisabled || canHover} $shadow={shadow}>
            <Thumbnail
                autoPlay
                videoUrl={animation_url}
                imageUrl={image_url}
                mashWidth={mashWidth}
                objectFitContain={objectFit === 'contain'}
            />

            <AnimatePresence>
                {rarityRank && !isHovering && !hideRarityRank ? (
                    <Rarity
                        $cardSize={cardSize}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        $alignRight={isMobile && !isOwner}
                    >
                        <RarityText>
                            {cardSize !== 'small' && 'Rank '}#{rarityRank}
                        </RarityText>
                    </Rarity>
                ) : null}
            </AnimatePresence>
        </Wrapper>
    );
    const pathChain = chain?.toLowerCase();
    const itemPath = `${pathChain}/${contract}/${encodeURIComponent(tokenId)}`;
    const useLink = !linkDisabled && itemPath && itemPath?.length > 3;

    return !useLink ? wrapperMarkup : <Link to={`/collectible/${itemPath}?goBack=1`}>{wrapperMarkup}</Link>;
};
